/**
 * useStylePresets Hook
 * Fetches user-created style presets from DB and merges with built-in STYLE_PRESETS.
 * Returns a combined record of all presets (name → description).
 *
 * Side effect: also patches the shared STYLE_PRESETS object so LLM services
 * (which import it directly from constants) see DB presets too.
 */

import { useState, useEffect, useCallback } from 'react';
import { STYLE_PRESETS } from '@/constants';
import { API_BASE } from '@/config/api';
import { fetchWithCsrf } from '@/utils/fetchWithCsrf';

interface StylePresetEntry {
  name: string;
  description: string;
  isBuiltIn?: boolean;
}

/**
 * Merged style presets: built-in + user-created from DB.
 * The returned `allPresets` record can be used anywhere STYLE_PRESETS was used before.
 */
export function useStylePresets() {
  const [dbPresets, setDbPresets] = useState<StylePresetEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchPresets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithCsrf(`${API_BASE}/api/style-presets/list`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const presets: StylePresetEntry[] = data.presets || [];
      setDbPresets(presets);

      // Patch the shared STYLE_PRESETS so LLM services see DB presets too
      for (const p of presets) {
        if (!p.isBuiltIn) {
          STYLE_PRESETS[p.name] = p.description;
        }
      }
    } catch (err) {
      console.error('Failed to fetch style presets:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPresets(); }, [fetchPresets]);

  // Merge: DB presets override static ones if same name exists
  const allPresets: Record<string, string> = { ...STYLE_PRESETS };
  for (const p of dbPresets) {
    allPresets[p.name] = p.description;
  }

  return { allPresets, loading, refetch: fetchPresets };
}
